import { z } from "zod";
/* ─── Reusable primitives ─── */
export const emailSchema = z
    .string()
    .min(1, "Email is required")
    .email("Invalid email address");
export const passwordSchema = z
    .string()
    .min(1, "Password is required")
    .min(6, "Password must be at least 6 characters");
export const ukPhoneSchema = z
    .string()
    .refine((v) => {
    const cleaned = v.replace(/\s/g, "");
    return /^(\+44|0)\d{10,11}$/.test(cleaned);
}, "Enter a valid UK phone number");
export const ukPostcodeSchema = z
    .string()
    .refine((v) => /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i.test(v.trim()), "Enter a valid UK postcode");
export const requiredString = (field) => z.string().min(1, `${field} is required`);
/* ─── Auth ─── */
export const loginSchema = z.object({
    email: emailSchema,
    password: passwordSchema,
});
export const baseRegisterSchema = z
    .object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string().min(1, "Confirm password is required"),
    displayName: requiredString("Name"),
    phoneNumber: z.string().optional(),
    postcode: z.string().optional(),
    companyName: z.string().optional(),
    vehicleType: z.string().optional(),
    licenseNumber: z.string().optional(),
})
    .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
});
/* ─── Landing Waitlist ─── */
export const driverWaitlistSchema = z.object({
    name: z.string().min(2, "Please enter your full name"),
    email: emailSchema,
    phone: ukPhoneSchema,
    postcode: ukPostcodeSchema,
    vehicleType: z.string().min(1, "Please select a vehicle type"),
    hearAboutUs: z.string().optional(),
    isOwnerDriver: z.boolean().optional(),
}).refine((data) => data.isOwnerDriver === true, {
    message: "You must confirm you are an owner-driver",
    path: ["isOwnerDriver"],
});
export const customerWaitlistSchema = z.object({
    name: z.string().min(2, "Please enter your name"),
    email: emailSchema,
    postcode: ukPostcodeSchema,
    serviceInterest: z.string().min(1, "Please select a service"),
    isBusiness: z.boolean().optional(),
    companyName: z.string().optional(),
});
/* ─── Profile ─── */
export const profileSchema = z.object({
    displayName: z.string().min(2, "Name must be at least 2 characters"),
    phoneNumber: z.union([ukPhoneSchema, z.literal("")]).optional(),
    postcode: z.union([ukPostcodeSchema, z.literal("")]).optional(),
});
/* ─── Messaging ─── */
export const messageSchema = z.object({
    text: z.string().min(1, "Message cannot be empty").max(2000, "Message too long"),
});
/* ─── Tickets ─── */
export const ticketSchema = z.object({
    subject: z.string().min(3).max(100),
    description: z.string().min(10).max(5000),
    category: z.enum(["support", "billing", "technical", "account", "other"]),
});
/* ─── Business ─── */
export const inviteDriverSchema = z.object({
    email: emailSchema,
    message: z.string().max(500, "Message too long").optional(),
});
export const jobPostingSchema = z.object({
    title: requiredString("Job title"),
    description: requiredString("Description"),
    vehicleType: z.string().optional(),
    licenseRequired: z.string().optional(),
    minExperienceMonths: z.coerce.number().min(0).optional(),
    minRating: z.coerce.number().min(0).max(5).optional(),
    compensationType: z.enum(["salary", "per_job", "hybrid"]),
    compensationAmount: z.coerce.number().positive("Amount must be greater than 0"),
    scheduleType: z.enum(["full_time", "part_time", "flexible"]),
    hoursPerWeek: z.coerce.number().min(1).max(80).optional(),
    maxApplicants: z.coerce.number().min(1).max(1000),
    plan: z.enum(["free", "starter", "growth", "pro", "enterprise"]),
    isFeatured: z.boolean().optional(),
});
export const adRequestSchema = z.object({
    title: z.string().min(5, "Title must be at least 5 characters").max(80),
    body: z.string().min(10, "Body must be at least 10 characters").max(300),
    ctaText: requiredString("CTA text"),
    destinationUrl: z.string().url("Enter a valid URL"),
    imageUrl: z.union([z.string().url(), z.literal("")]).optional(),
    roles: z.array(z.string()).min(1, "Select at least one target audience"),
    slots: z.array(z.string()).min(1, "Select at least one ad slot"),
    geofence: z.string().optional(),
    tier: z.enum(["bronze", "silver", "gold", "platinum"]),
    bidAmount: z.number().min(100, "Minimum bid is 100p"),
    dailyBudget: z.number().min(100, "Minimum daily budget is 100p"),
    totalBudget: z.number().min(100, "Minimum total budget is 100p"),
    startDate: z.string().min(1, "Start date is required"),
    endDate: z.string().min(1, "End date is required"),
});
/* ─── Booking ─── */
export const addressSchema = z.object({
    line1: requiredString("Address line 1"),
    city: requiredString("City"),
    postcode: ukPostcodeSchema,
});
export const genericBookingSchema = z.object({
    pickup: addressSchema,
    dropoff: addressSchema,
    date: z.string().min(1, "Please select a date"),
    notes: z.string().max(1000, "Notes too long").optional(),
});
export const moveStep1Schema = z.object({
    pickup: addressSchema,
    dropoff: addressSchema,
    date: z.string().min(1, "Please select a move date"),
    moveTime: z.string().min(1, "Please select a time"),
});
//# sourceMappingURL=schemas.js.map